import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Business as DistrictIcon,
  AccountTree as SubdivisionIcon,
  Domain as BlockIcon,
  Group as GPUIcon,
  Home as WardIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import RoleGuard from '../../components/Auth/RoleGuard';
import { districtService } from '../../services/districtService';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`district-tabpanel-${index}`}
    aria-labelledby={`district-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const DistrictManagement = () => {
  const [tabValue, setTabValue] = useState(0);
  const [open, setOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'district', 'subdivision', 'block', 'gpu', 'ward'
  const [editItem, setEditItem] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedSubdivision, setSelectedSubdivision] = useState('');
  const [selectedBlock, setSelectedBlock] = useState('');
  const [selectedGPU, setSelectedGPU] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    district_id: '',
    subdivision_id: '',
    block_id: '',
    gpu_id: '',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const queryClient = useQueryClient();

  // Fetch queries
  const { data: districts = [], isLoading: loadingDistricts, refetch: refetchDistricts } = useQuery({
    queryKey: ['districts'],
    queryFn: districtService.getDistricts,
  });

  const { data: subdivisions = [], isLoading: loadingSubdivisions } = useQuery({
    queryKey: ['subdivisions', selectedDistrict],
    queryFn: () => selectedDistrict ? districtService.getSubdivisions('id', selectedDistrict) : Promise.resolve([]),
    enabled: !!selectedDistrict,
  });

  const { data: blocks = [], isLoading: loadingBlocks } = useQuery({
    queryKey: ['blocks', selectedSubdivision],
    queryFn: () => selectedSubdivision ? districtService.getBlocks(selectedSubdivision) : Promise.resolve([]),
    enabled: !!selectedSubdivision,
  });

  const { data: gpus = [], isLoading: loadingGPUs } = useQuery({
    queryKey: ['gpus', selectedBlock],
    queryFn: () => selectedBlock ? districtService.getGPUs(selectedBlock) : Promise.resolve([]),
    enabled: !!selectedBlock,
  });

  const { data: wards = [], isLoading: loadingWards } = useQuery({
    queryKey: ['wards', selectedGPU],
    queryFn: () => selectedGPU ? districtService.getWards(selectedGPU) : Promise.resolve([]),
    enabled: !!selectedGPU,
  });

  // Mutations
  const addMutation = useMutation({
    mutationFn: (data) => {
      switch (dialogType) {
        case 'district':
          return districtService.addDistrict(data);
        case 'subdivision':
          return districtService.addSubdivision(data);
        case 'block':
          return districtService.addBlock(data);
        case 'gpu':
          return districtService.addGPU(data);
        case 'ward':
          return districtService.addWard(data);
        default:
          throw new Error('Invalid dialog type');
      }
    },
    onSuccess: () => {
      toast.success(`${dialogType.charAt(0).toUpperCase() + dialogType.slice(1)} added successfully!`);
      setOpen(false);
      resetForm();
      queryClient.invalidateQueries(['districts']);
      queryClient.invalidateQueries(['subdivisions']);
      queryClient.invalidateQueries(['blocks']);
      queryClient.invalidateQueries(['gpus']);
      queryClient.invalidateQueries(['wards']);
    },
    onError: (error) => {
      toast.error(`Error adding ${dialogType}: ${error.response?.data?.message || error.message}`);
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      district_id: '',
      subdivision_id: '',
      block_id: '',
      gpu_id: '',
    });
    setEditItem(null);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedDistrict('');
    setSelectedSubdivision('');
    setSelectedBlock('');
    setSelectedGPU('');
  };

  const handleOpenDialog = (type, item = null) => {
    setDialogType(type);
    setEditItem(item);
    if (item) {
      setFormData({
        name: item.name || '',
        code: item.code || '',
        district_id: item.district_id || selectedDistrict,
        subdivision_id: item.subdivision_id || selectedSubdivision,
        block_id: item.block_id || selectedBlock,
        gpu_id: item.gpu_id || selectedGPU,
      });
    } else {
      resetForm();
      // Set parent IDs based on current selections
      setFormData(prev => ({
        ...prev,
        district_id: selectedDistrict,
        subdivision_id: selectedSubdivision,
        block_id: selectedBlock,
        gpu_id: selectedGPU,
      }));
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    resetForm();
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    const submitData = { ...formData };
    
    // Remove empty fields
    Object.keys(submitData).forEach(key => {
      if (submitData[key] === '') {
        delete submitData[key];
      }
    });

    addMutation.mutate(submitData);
  };

  const renderDistrictsTab = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Districts</Typography>
        <Box>
          <IconButton onClick={refetchDistricts} disabled={loadingDistricts}>
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('district')}
            sx={{ ml: 1 }}
          >
            Add District
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Subdivisions</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingDistricts ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : districts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No districts found
                </TableCell>
              </TableRow>
            ) : (
              districts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((district) => (
                  <TableRow key={district.id} hover>
                    <TableCell>{district.id}</TableCell>
                    <TableCell>{district.name}</TableCell>
                    <TableCell>
                      <Chip label={district.code} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{district.subdivision_count || 0}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog('district', district)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={districts.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>
    </Box>
  );

  const renderHierarchicalTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Administrative Hierarchy Management
      </Typography>
      
      {/* Breadcrumb Navigation */}
      <Box mb={3}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link
            component="button"
            variant="body1"
            onClick={() => {
              setSelectedDistrict('');
              setSelectedSubdivision('');
              setSelectedBlock('');
              setSelectedGPU('');
            }}
          >
            All Districts
          </Link>
          {selectedDistrict && (
            <Link
              component="button"
              variant="body1"
              onClick={() => {
                setSelectedSubdivision('');
                setSelectedBlock('');
                setSelectedGPU('');
              }}
            >
              {districts.find(d => d.id === parseInt(selectedDistrict))?.name || 'District'}
            </Link>
          )}
          {selectedSubdivision && (
            <Link
              component="button"
              variant="body1"
              onClick={() => {
                setSelectedBlock('');
                setSelectedGPU('');
              }}
            >
              {subdivisions.find(s => s.id === parseInt(selectedSubdivision))?.name || 'Subdivision'}
            </Link>
          )}
          {selectedBlock && (
            <Link
              component="button"
              variant="body1"
              onClick={() => setSelectedGPU('')}
            >
              {blocks.find(b => b.id === parseInt(selectedBlock))?.name || 'Block'}
            </Link>
          )}
          {selectedGPU && (
            <Typography color="text.primary">
              {gpus.find(g => g.id === parseInt(selectedGPU))?.name || 'GPU'}
            </Typography>
          )}
        </Breadcrumbs>
      </Box>

      <Grid container spacing={3}>
        {/* Selection Controls */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Navigate Hierarchy
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>District</InputLabel>
                    <Select
                      value={selectedDistrict}
                      onChange={(e) => {
                        setSelectedDistrict(e.target.value);
                        setSelectedSubdivision('');
                        setSelectedBlock('');
                        setSelectedGPU('');
                      }}
                      label="District"
                    >
                      <MenuItem value="">All Districts</MenuItem>
                      {districts.map((district) => (
                        <MenuItem key={district.id} value={district.id}>
                          {district.name} ({district.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {selectedDistrict && (
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Subdivision</InputLabel>
                      <Select
                        value={selectedSubdivision}
                        onChange={(e) => {
                          setSelectedSubdivision(e.target.value);
                          setSelectedBlock('');
                          setSelectedGPU('');
                        }}
                        label="Subdivision"
                        disabled={loadingSubdivisions}
                      >
                        <MenuItem value="">All Subdivisions</MenuItem>
                        {subdivisions.map((subdivision) => (
                          <MenuItem key={subdivision.id} value={subdivision.id}>
                            {subdivision.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                
                {selectedSubdivision && (
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Block</InputLabel>
                      <Select
                        value={selectedBlock}
                        onChange={(e) => {
                          setSelectedBlock(e.target.value);
                          setSelectedGPU('');
                        }}
                        label="Block"
                        disabled={loadingBlocks}
                      >
                        <MenuItem value="">All Blocks</MenuItem>
                        {blocks.map((block) => (
                          <MenuItem key={block.id} value={block.id}>
                            {block.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                
                {selectedBlock && (
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>GPU</InputLabel>
                      <Select
                        value={selectedGPU}
                        onChange={(e) => setSelectedGPU(e.target.value)}
                        label="GPU"
                        disabled={loadingGPUs}
                      >
                        <MenuItem value="">All GPUs</MenuItem>
                        {gpus.map((gpu) => (
                          <MenuItem key={gpu.id} value={gpu.id}>
                            {gpu.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Content based on selection */}
        <Grid item xs={12}>
          {!selectedDistrict && renderDistrictsList()}
          {selectedDistrict && !selectedSubdivision && renderSubdivisionsList()}
          {selectedSubdivision && !selectedBlock && renderBlocksList()}
          {selectedBlock && !selectedGPU && renderGPUsList()}
          {selectedGPU && renderWardsList()}
        </Grid>
      </Grid>
    </Box>
  );

  const renderDistrictsList = () => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" display="flex" alignItems="center">
            <DistrictIcon sx={{ mr: 1 }} />
            Districts
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('district')}
          >
            Add District
          </Button>
        </Box>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Subdivisions</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingDistricts ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : districts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No districts found
                  </TableCell>
                </TableRow>
              ) : (
                districts
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((district) => (
                    <TableRow 
                      key={district.id} 
                      hover 
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setSelectedDistrict(district.id)}
                    >
                      <TableCell>{district.id}</TableCell>
                      <TableCell>{district.name}</TableCell>
                      <TableCell>
                        <Chip label={district.code} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{district.subdivision_count || 0}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDialog('district', district);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={districts.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      </CardContent>
    </Card>
  );

  const renderSubdivisionsList = () => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" display="flex" alignItems="center">
            <SubdivisionIcon sx={{ mr: 1 }} />
            Subdivisions in {districts.find(d => d.id === parseInt(selectedDistrict))?.name}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('subdivision')}
          >
            Add Subdivision
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Blocks</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingSubdivisions ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : subdivisions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No subdivisions found
                  </TableCell>
                </TableRow>
              ) : (
                subdivisions.map((subdivision) => (
                  <TableRow 
                    key={subdivision.id} 
                    hover 
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelectedSubdivision(subdivision.id)}
                  >
                    <TableCell>{subdivision.id}</TableCell>
                    <TableCell>{subdivision.name}</TableCell>
                    <TableCell>{subdivision.block_count || 0}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDialog('subdivision', subdivision);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  const renderBlocksList = () => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" display="flex" alignItems="center">
            <BlockIcon sx={{ mr: 1 }} />
            Blocks in {subdivisions.find(s => s.id === parseInt(selectedSubdivision))?.name}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('block')}
          >
            Add Block
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>GPUs</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingBlocks ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : blocks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No blocks found
                  </TableCell>
                </TableRow>
              ) : (
                blocks.map((block) => (
                  <TableRow 
                    key={block.id} 
                    hover 
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelectedBlock(block.id)}
                  >
                    <TableCell>{block.id}</TableCell>
                    <TableCell>{block.name}</TableCell>
                    <TableCell>{block.gpu_count || 0}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDialog('block', block);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  const renderGPUsList = () => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" display="flex" alignItems="center">
            <GPUIcon sx={{ mr: 1 }} />
            GPUs in {blocks.find(b => b.id === parseInt(selectedBlock))?.name}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('gpu')}
          >
            Add GPU
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Wards</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingGPUs ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : gpus.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No GPUs found
                  </TableCell>
                </TableRow>
              ) : (
                gpus.map((gpu) => (
                  <TableRow 
                    key={gpu.id} 
                    hover 
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelectedGPU(gpu.id)}
                  >
                    <TableCell>{gpu.id}</TableCell>
                    <TableCell>{gpu.name}</TableCell>
                    <TableCell>{gpu.ward_count || 0}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDialog('gpu', gpu);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  const renderWardsList = () => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" display="flex" alignItems="center">
            <WardIcon sx={{ mr: 1 }} />
            Wards in {gpus.find(g => g.id === parseInt(selectedGPU))?.name}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('ward')}
          >
            Add Ward
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingWards ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : wards.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No wards found
                  </TableCell>
                </TableRow>
              ) : (
                wards.map((ward) => (
                  <TableRow key={ward.id} hover>
                    <TableCell>{ward.id}</TableCell>
                    <TableCell>{ward.name}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog('ward', ward)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  const renderDialog = () => {
    const getDialogTitle = () => {
      const action = editItem ? 'Edit' : 'Add';
      return `${action} ${dialogType.charAt(0).toUpperCase() + dialogType.slice(1)}`;
    };

    const getFormFields = () => {
      switch (dialogType) {
        case 'district':
          return (
            <>
              <TextField
                fullWidth
                label="District Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="District Code"
                value={formData.code}
                onChange={(e) => handleFormChange('code', e.target.value)}
                margin="normal"
                required
                helperText="Use standard district codes (e.g., GT, MN, etc.)"
              />
            </>
          );
        case 'subdivision':
          return (
            <>
              <TextField
                fullWidth
                label="Subdivision Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                margin="normal"
                required
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>District</InputLabel>
                <Select
                  value={formData.district_id}
                  onChange={(e) => handleFormChange('district_id', e.target.value)}
                  label="District"
                  required
                >
                  {districts.map((district) => (
                    <MenuItem key={district.id} value={district.id}>
                      {district.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          );
        case 'block':
          return (
            <>
              <TextField
                fullWidth
                label="Block Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                margin="normal"
                required
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Subdivision</InputLabel>
                <Select
                  value={formData.subdivision_id}
                  onChange={(e) => handleFormChange('subdivision_id', e.target.value)}
                  label="Subdivision"
                  required
                >
                  {subdivisions.map((subdivision) => (
                    <MenuItem key={subdivision.id} value={subdivision.id}>
                      {subdivision.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          );
        case 'gpu':
          return (
            <>
              <TextField
                fullWidth
                label="GPU Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                margin="normal"
                required
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Block</InputLabel>
                <Select
                  value={formData.block_id}
                  onChange={(e) => handleFormChange('block_id', e.target.value)}
                  label="Block"
                  required
                >
                  {blocks.map((block) => (
                    <MenuItem key={block.id} value={block.id}>
                      {block.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          );
        case 'ward':
          return (
            <>
              <TextField
                fullWidth
                label="Ward Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                margin="normal"
                required
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>GPU</InputLabel>
                <Select
                  value={formData.gpu_id}
                  onChange={(e) => handleFormChange('gpu_id', e.target.value)}
                  label="GPU"
                  required
                >
                  {gpus.map((gpu) => (
                    <MenuItem key={gpu.id} value={gpu.id}>
                      {gpu.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          );
        default:
          return null;
      }
    };

    return (
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent>
          {getFormFields()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={addMutation.isLoading || !formData.name}
          >
            {addMutation.isLoading ? <CircularProgress size={24} /> : editItem ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <RoleGuard allowedRoles={['admin']}>
      <Box>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          District Management
        </Typography>
        
        <Typography variant="body1" color="text.secondary" mb={3}>
          Manage administrative hierarchy: Districts, Subdivisions, Blocks, GPUs, and Wards
        </Typography>

        <Card sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Districts Overview" />
            <Tab label="Hierarchical Management" />
          </Tabs>
          
          <TabPanel value={tabValue} index={0}>
            {renderDistrictsTab()}
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            {renderHierarchicalTab()}
          </TabPanel>
        </Card>

        {renderDialog()}
      </Box>
    </RoleGuard>
  );
};

export default DistrictManagement;
